import { Box, Divider, Link, Stack, Typography } from '@mui/material'
import { palette } from 'c1ui'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CurrentIcon } from 'components/core/icons/current'
import { IncompleteIcon } from 'components/core/icons/incomplete'
import { ExternalLink } from 'components/core/links/external-link'
import { SmartTabsContext } from 'components/core/tabs/index'
import { getOutcome } from 'components/core/task/outcome/getOutcomeText'
import { getShortString } from 'components/core/time/display/index'
import { roundedDurationString } from 'components/core/time/duration/duration'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name/index'
import { linkLabel } from 'components/pages/task/details/content/TaskCurrentStep'
import {
    currentStepInstance,
    getApprovalTypei18n,
    getManualProvisionFallbackErrori18n, getProvisionTypei18n, stepsFromInstance,
    StepWithStepInstance,
} from 'components/pages/task/details/policy-workflow'
import {
    DisplayActionableUserList,
} from 'components/pages/task/details/task-content-v2/task-review-details/TaskReviewDetailsComponents'
import { TaskValue } from 'lib/actions/task/index'
import { ArrowRight, Check, CircleAlert, Info as InfoIcon, Minus, SkipForward, X } from 'lucide-react'
import { ExternalTicket, ExternalTicketState } from 'pbts/c1/api/external_tickets/v1/external_tickets'
import {
    ActionInstance,
    ActionInstanceState,
    Provision,
    ProvisionInstance,
    ProvisionInstanceState,
} from 'pbts/c1/api/policy/v1/policy'
import { FormInstance, FormInstanceState } from 'pbts/c1/api/policy/v1/policy_step_form'
import { ApprovalInstance, ApprovalInstanceState } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { Wait, WaitInstance, WaitInstance_WaitInstanceState } from 'pbts/c1/api/policy/v1/policy_steps_wait'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import React, { FC, ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { GetExternalTicketApi } from 'redux_toolkit/external_tickets'
import { getUserApi } from 'redux_toolkit/users'

export type ApprovalChainProps = {
    task: TaskValue
}

export type ProvisionChainProps = {
    task: TaskValue
}

export type StepTarget = {
    appId: string
    entitlementId: string
} | null

type ActionInfoProps = {
    userIds: string[]
    message: ReactNode
}

const Info: FC<ActionInfoProps> = ({
    message,
    userIds,
}) => {
    const { data: user, isLoading, error } = getUserApi.useGet({ id: userIds[0] })

    if (isLoading) return <C1CircularProgress />

    if (error) return <MinimalAPIError err={error.message} />

    if (user) {
        return (
            <Typography variant="caption">
                {message}
                <FormattedMessage
                    id="components_task_action_info"
                    defaultMessage=" by {name}."
                    values={{
                        name: user.displayName,
                    }}
                />
            </Typography>
        )
    }

    return <Typography variant="caption">{message}</Typography>
}

type GetActionInfoProps = {
    currentStepApprovalInstance?: ApprovalInstance
}

const ActionInfo: FC<GetActionInfoProps> = ({
    currentStepApprovalInstance,
}) => {
    if (currentStepApprovalInstance?.state !== ApprovalInstanceState.DONE) {
        return null
    }
    if (currentStepApprovalInstance?.outcome?.oneofKind === 'approved') {
        const approvedAt = currentStepApprovalInstance?.outcome?.approved?.approvedAt
        const approvedUserId = [currentStepApprovalInstance?.outcome?.approved?.userId]
        if (!approvedAt) {
            return null
        }
        return <Info
            userIds={approvedUserId}
            message={
                <FormattedMessage
                    id="components_task_review_approval_chain_info_approved_on"
                    defaultMessage="Approved on {date}"
                    values={{
                        date: getShortString({
                            d: pbtsTimestamp.toDate(approvedAt),
                            variant: 'dense',
                        }),
                    }}
                />
            }
        />
    }
    if (currentStepApprovalInstance?.outcome?.oneofKind === 'denied') {
        const deniedAt = currentStepApprovalInstance?.outcome?.denied?.deniedAt
        const deniedUserId = [currentStepApprovalInstance?.outcome?.denied?.userId]
        if (!deniedAt) {
            return null
        }
        return <Info
            userIds={deniedUserId}
            message={
                <FormattedMessage
                    id="components_task_review_approval_chain_info_denied_on"
                    defaultMessage="Denied on {date}"
                    values={{
                        date: getShortString({
                            d: pbtsTimestamp.toDate(deniedAt),
                            variant: 'dense',
                        }),
                    }}
                />
            }
        />
    }
    if (currentStepApprovalInstance?.outcome?.oneofKind === 'reassigned') {
        const reassignedAt = currentStepApprovalInstance?.outcome?.reassigned?.reassignedAt
        const reassignedUserId = [currentStepApprovalInstance?.outcome?.reassigned?.userId]
        if (!reassignedAt) {
            return null
        }
        return <Info
            userIds={reassignedUserId}
            message={
                <FormattedMessage
                    id="components_task_review_approval_chain_info_reassigned_on"
                    defaultMessage="Reassigned on {date}"
                    values={{
                        date: getShortString({
                            d: pbtsTimestamp.toDate(reassignedAt),
                            variant: 'dense',
                        }),
                    }}
                />
            }
        />
    }
    if (currentStepApprovalInstance?.outcome?.oneofKind === 'skipped') {
        const skippedAt = currentStepApprovalInstance?.outcome?.skipped?.skippedAt
        const skippedUserId = [currentStepApprovalInstance?.outcome?.skipped?.userId]
        if (!skippedAt) {
            return null
        }
        return <Info
            userIds={skippedUserId}
            message={
                <FormattedMessage
                    id="components_task_review_approval_chain_info_skipped_on"
                    defaultMessage="Skipped on {date}"
                    values={{
                        date: getShortString({
                            d: pbtsTimestamp.toDate(skippedAt),
                            variant: 'dense',
                        }),
                    }}
                />
            }
        />
    }
    return null
}

const CompletedProvisionOutcomeInfo: FC<{
    currentStepProvisionInstance: ProvisionInstance
}> = ({
    currentStepProvisionInstance,
}) => {
    if (currentStepProvisionInstance?.outcome?.oneofKind !== 'completed') {
        return null
    }
    const completedAt = currentStepProvisionInstance?.outcome?.completed?.completedAt
    const completedUserId = [currentStepProvisionInstance?.outcome?.completed?.userId]
    if (!completedAt) {
        return null
    }
    return <Info
        userIds={completedUserId}
        message={
            <FormattedMessage
                id="components_task_review_approval_chain_info_completed_on"
                defaultMessage="Completed on {date}"
                values={{
                    date: getShortString({
                        d: pbtsTimestamp.toDate(completedAt),
                        variant: 'dense',
                    }),
                }}
            />
        }
    />
}

const SkippedProvisionOutcomeInfo: FC<{
    currentStepProvisionInstance: ProvisionInstance
}> = ({
    currentStepProvisionInstance,
}) => {
    if (currentStepProvisionInstance?.outcome?.oneofKind !== 'skipped') {
        return null
    }
    const skippedAt = currentStepProvisionInstance?.outcome?.skipped?.skippedAt
    const skippedUserId = [currentStepProvisionInstance?.outcome?.skipped?.userId]
    if (!skippedAt) {
        return null
    }
    return <Info
        userIds={skippedUserId}
        message={
            <FormattedMessage
                id="components_task_review_approval_chain_info_skipped_on"
                defaultMessage="Skipped on {date}"
                values={{
                    date: getShortString({
                        d: pbtsTimestamp.toDate(skippedAt),
                        variant: 'dense',
                    }),
                }}
            />
        }
    />
}

const CancelledProvisionOutcomeInfo: FC<{
    currentStepProvisionInstance: ProvisionInstance
}> = ({
    currentStepProvisionInstance,
}) => {
    if (currentStepProvisionInstance?.outcome?.oneofKind !== 'cancelled') {
        return null
    }

    const completedAt = currentStepProvisionInstance?.outcome?.cancelled?.cancelledAt
    const completedUserId = [currentStepProvisionInstance?.outcome?.cancelled?.cancelledByUserId]
    if (!completedAt) {
        return null
    }
    return <Info
        userIds={completedUserId}
        message={
            <FormattedMessage
                id="components_task_review_approval_chain_info_cancelled_on"
                defaultMessage="cancelled on {date}"
                values={{
                    date: getShortString({
                        d: pbtsTimestamp.toDate(completedAt),
                        variant: 'dense',
                    }),
                }}
            />
        }
    />
}

const ProvisionInfo: FC<{
    currentStepProvisionInstance: ProvisionInstance
}> = ({
    currentStepProvisionInstance,
}) => {
    if (currentStepProvisionInstance?.state !== ProvisionInstanceState.DONE) {
        return null
    }
    if (currentStepProvisionInstance?.outcome?.oneofKind === 'completed') {
        return <CompletedProvisionOutcomeInfo currentStepProvisionInstance={currentStepProvisionInstance} />
    } if (currentStepProvisionInstance?.outcome?.oneofKind === 'cancelled') {
        return <CancelledProvisionOutcomeInfo currentStepProvisionInstance={currentStepProvisionInstance} />
    } if (currentStepProvisionInstance?.outcome?.oneofKind === 'skipped') {
        return <SkippedProvisionOutcomeInfo currentStepProvisionInstance={currentStepProvisionInstance} />
    }
    return null
}

const WaitInfo: FC<{
    currentWaitInstance?: WaitInstance
}> = ({
    currentWaitInstance,
}) => {
    if (!currentWaitInstance) { return null }

    switch (currentWaitInstance?.outcome.oneofKind) {
        case 'succeeded':
            if (!currentWaitInstance.outcome.succeeded.succeededAt) {
                return null
            }
            return <Typography variant="caption">
                <FormattedMessage
                    id="wait_instance_completed"
                    defaultMessage="Completed on {completed}"
                    values={{
                        completed: getShortString({
                            d: pbtsTimestamp.toDate(currentWaitInstance.outcome.succeeded.succeededAt),
                            variant: 'dense',
                        }),
                    }}
                />
            </Typography>
        case 'timedOut':
            if (!currentWaitInstance.outcome.timedOut.timedOutAt) {
                return null
            }
            return <Typography variant="caption">
                <FormattedMessage
                    id="wait_instance_timed_out"
                    defaultMessage="Timed out on {timed_out}"
                    values={{
                        timed_out: getShortString({
                            d: pbtsTimestamp.toDate(currentWaitInstance.outcome.timedOut.timedOutAt),
                            variant: 'dense',
                        }),
                    }}
                />
            </Typography>
        default:
            return null
    }
}

const CompletedFormOutcomeInfo: FC<{
    currentFormInstance: FormInstance
}> = ({
    currentFormInstance,
}) => {
    if (currentFormInstance?.outcome?.oneofKind !== 'completed') {
        return null
    }
    const completedAt = currentFormInstance?.outcome?.completed?.completedAt
    const completedUserId = [currentFormInstance?.outcome?.completed?.userId]
    if (!completedAt) {
        return null
    }
    return <Info
        userIds={completedUserId}
        message={
            <FormattedMessage
                id="components_task_review_approval_chain_form_info_completed_on"
                defaultMessage="Completed form data: <b>{name}</b> on {date}"
                values={{
                    date: getShortString({
                        d: pbtsTimestamp.toDate(completedAt),
                        variant: 'dense',
                    }),
                    name: currentFormInstance.form?.displayName,
                    b: (chunks) => <b style={{ fontWeight: '600' }}>{chunks}</b>,
                }}
            />
        }
    />
}

const SkippedFormOutcomeInfo: FC<{
    currentFormInstance: FormInstance
}> = ({
    currentFormInstance,
}) => {
    if (currentFormInstance?.outcome?.oneofKind !== 'skipped') {
        return null
    }
    const skippedAt = currentFormInstance?.outcome?.skipped?.skippedAt
    const skippedUserId = [currentFormInstance?.outcome?.skipped?.userId]
    if (!skippedAt) {
        return null
    }
    return <Info
        userIds={skippedUserId}
        message={
            <FormattedMessage
                id="components_task_review_approval_chain_form_info_skipped_on"
                defaultMessage="Request form: <b>{name}</b> skipped on {date}"
                values={{
                    name: currentFormInstance.form?.displayName,
                    date: getShortString({
                        d: pbtsTimestamp.toDate(skippedAt),
                        variant: 'dense',
                    }),
                    b: (chunks) => <b style={{ fontWeight: '600' }}>{chunks}</b>,
                }}
            />
        }
    />
}

const ReassignedFormOutcomeInfo: FC<{
    currentFormInstance: FormInstance
}> = ({
    currentFormInstance,
}) => {
    if (currentFormInstance?.outcome?.oneofKind !== 'reassigned') {
        return null
    }
    const reassignedAt = currentFormInstance?.outcome?.reassigned?.reassignedAt
    const reassignedUserId = [currentFormInstance?.outcome?.reassigned?.userId]
    if (!reassignedAt) {
        return null
    }
    return <Info
        userIds={reassignedUserId}
        message={
            <FormattedMessage
                id="components_task_review_approval_chain_info_reassigned_on"
                defaultMessage="Reassigned on {date}"
                values={{
                    date: getShortString({
                        d: pbtsTimestamp.toDate(reassignedAt),
                        variant: 'dense',
                    }),
                }}
            />
        }
    />
}

const FormInfo: FC<{
    currentFormInstance?: FormInstance
}> = ({
    currentFormInstance,
}) => {
    if (!currentFormInstance || currentFormInstance.state !== FormInstanceState.DONE) {
        return null
    }

    switch (currentFormInstance?.outcome?.oneofKind) {
        case 'skipped':
            return <SkippedFormOutcomeInfo currentFormInstance={currentFormInstance} />
        case 'reassigned':
            return <ReassignedFormOutcomeInfo currentFormInstance={currentFormInstance} />
        case 'completed':
        default:
            return <CompletedFormOutcomeInfo currentFormInstance={currentFormInstance} />
    }
}

const ActionStepInfo: FC<{
    currentActionInstance?: ActionInstance
}> = ({
    currentActionInstance,
}) => {
    if (!currentActionInstance) {
        return null
    }

    // Show info for completed states only (errors handled by ShowActionError)
    if (currentActionInstance.state !== ActionInstanceState.DONE) {
        return null
    }

    switch (currentActionInstance?.outcome?.oneofKind) {
        case 'success':
            if (!currentActionInstance.outcome.success.outcomeTime) {
                return null
            }
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="components_task_review_action_step_info_completed_on"
                        defaultMessage="Action completed on {date}"
                        values={{
                            date: getShortString({
                                d: pbtsTimestamp.toDate(currentActionInstance.outcome.success.outcomeTime),
                                variant: 'dense',
                            }),
                        }}
                    />
                </Typography>
            )
        case 'denied':
            if (!currentActionInstance.outcome.denied.outcomeTime) {
                return null
            }
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="components_task_review_action_step_info_denied_on"
                        defaultMessage="Action denied on {date}"
                        values={{
                            date: getShortString({
                                d: pbtsTimestamp.toDate(currentActionInstance.outcome.denied.outcomeTime),
                                variant: 'dense',
                            }),
                        }}
                    />
                </Typography>
            )
        case 'cancelled':
            if (!currentActionInstance.outcome.cancelled.outcomeTime) {
                return null
            }
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="components_task_review_action_step_info_cancelled_on"
                        defaultMessage="Action cancelled on {date}"
                        values={{
                            date: getShortString({
                                d: pbtsTimestamp.toDate(currentActionInstance.outcome.cancelled.outcomeTime),
                                variant: 'dense',
                            }),
                        }}
                    />
                </Typography>
            )
        default:
            return null
    }
}

type CircleIndicatorProps = {
    color: string,
    children: React.ReactNode,
    title?: string,
    noBackground?: boolean
}

const CircleIndicator: FC<CircleIndicatorProps> = ({ color, children, title, noBackground }) => (
    <Stack
        sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: noBackground ? 'transparent' : color,
            color: (theme) => (noBackground ? color : theme.palette.getContrastText(color)),
        }}
        alignItems="center"
        justifyContent="center"
        title={title}
    >
        {children}
    </Stack>
)

const ApprovedIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.success.main}
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_approved',
            defaultMessage: 'Approved',
        })}
    >
        <C1Icon icon={Check} size="sm" />
    </CircleIndicator>
}

const ReassignedIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.warning.light}
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_reassigned',
            defaultMessage: 'Approved',
        })}
    >
        <C1Icon icon={ArrowRight} size="sm" />
    </CircleIndicator>
}

const DeniedIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.error.main}
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_denied',
            defaultMessage: 'Denied',
        })}
    >
        <C1Icon icon={Minus} size="sm" />
    </CircleIndicator>
}

const SkippedIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.info.main}
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_skipped',
            defaultMessage: 'Skipped',
        })}
    >
        <C1Icon icon={SkipForward} size="sm" />
    </CircleIndicator>
}

const ErroredIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.error.main}
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_errored',
            defaultMessage: 'Errored',
        })}
    >
        <C1Icon icon={CircleAlert} size="sm" />
    </CircleIndicator>
}

const CancelledIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.info.light}
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_cancelled',
            defaultMessage: 'Cancelled',
        })}
    >
        <C1Icon icon={X} size="sm" />
    </CircleIndicator>
}

const InfoIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.info.light}
        noBackground
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_info',
            defaultMessage: 'Info',
        })}
    >
        <C1Icon icon={InfoIcon} size="md" />
    </CircleIndicator>
}

const IncompleteIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.info.light}
        noBackground
        title={intl.formatMessage({
            id: 'components_task_review_approval_chain_info_incomplete',
            defaultMessage: 'Incomplete',
        })}
    >
        <IncompleteIcon fontSize="medium" />
    </CircleIndicator>
}

const CurrentIndicator: FC = () => {
    const intl = useIntl()
    return <CircleIndicator
        color={palette.primary.main}
        noBackground
        title={intl.formatMessage({
            id: 'components_core_icons_current_step',
            defaultMessage: 'Current step',
        })}
    >
        <CurrentIcon fontSize="medium" />
    </CircleIndicator>
}

const ApprovalInstanceIndicator: FC<{
    approvalInstance?: ApprovalInstance,
    isCurrentStep: boolean,
    taskIsClosed: boolean
}> = ({
    approvalInstance,
    isCurrentStep,
    taskIsClosed,
}) => {
    if (isCurrentStep) {
        return !taskIsClosed ? <CurrentIndicator /> : <CancelledIndicator />
    }
    switch (approvalInstance?.state) {
        case ApprovalInstanceState.DONE:
            switch (approvalInstance?.outcome.oneofKind) {
                case 'denied':
                    return <DeniedIndicator />
                case 'skipped':
                    return <SkippedIndicator />
                case 'reassigned':
                    return <ReassignedIndicator />
                default:
                    return <ApprovedIndicator />
            }
        case ApprovalInstanceState.INIT:
        case ApprovalInstanceState.SENDING_NOTIFICATIONS:
            return <ApprovedIndicator />
        case ApprovalInstanceState.WAITING:
        default:
            return !taskIsClosed ? <IncompleteIndicator /> : <CancelledIndicator />
    }
}

const WaitInstanceIndicator: FC<{
    waitInstance?: WaitInstance,
    isCurrentStep: boolean,
    taskIsClosed: boolean
}> = ({
    waitInstance,
    isCurrentStep,
    taskIsClosed,
}) => {
    switch (waitInstance?.state) {
        case WaitInstance_WaitInstanceState.COMPLETED:
            switch (waitInstance?.outcome.oneofKind) {
                case 'skipped':
                    return <SkippedIndicator />
                default:
                    return <ApprovedIndicator />
            }
        case WaitInstance_WaitInstanceState.TIMED_OUT:
            return <DeniedIndicator />
        case WaitInstance_WaitInstanceState.WAITING:
            if (isCurrentStep) {
                return !taskIsClosed ? <CurrentIndicator /> : <CancelledIndicator />
            }
                // This only happens if the step was cancelled or restarted but is still visible
            return <DeniedIndicator />
        default:
            return !taskIsClosed ? <IncompleteIcon fontSize="small" /> : <CancelledIndicator />
    }
}

const ProvisionInstanceIndicator: FC<{
    provisionInstance?: ProvisionInstance,
    isCurrentStep: boolean,
    taskIsClosed: boolean
}> = ({
    provisionInstance,
    isCurrentStep,
    taskIsClosed,
}) => {
    if (isCurrentStep && provisionInstance?.state !== ProvisionInstanceState.DONE) {
        return !taskIsClosed ? <CurrentIndicator /> : <CancelledIndicator />
    }
    switch (provisionInstance?.state) {
        case ProvisionInstanceState.DONE:
            switch (provisionInstance.outcome.oneofKind) {
                case 'cancelled':
                    return <CancelledIndicator />
                case 'completed':
                    return <ApprovedIndicator />
                case 'skipped':
                    return <SkippedIndicator />
                case 'errored':
                    if (provisionInstance.outcome.errored.errorCode === 'PEC-1') {
                        return <InfoIndicator />
                    }
                    return <ErroredIndicator />
                default:
                    return <DeniedIndicator />
            }
        case ProvisionInstanceState.SENDING_NOTIFICATIONS:
            return <ApprovedIndicator />
        case ProvisionInstanceState.WAITING:
        case ProvisionInstanceState.INIT:
        case ProvisionInstanceState.CREATE_CONNECTOR_ACTIONS_FOR_TARGET:
        default:
            return !taskIsClosed ? <IncompleteIndicator /> : <CancelledIndicator />
    }
}

const FormInstanceIndicator: FC<{
    formInstance?: FormInstance,
    isCurrentStep: boolean,
    taskIsClosed: boolean
}> = ({
    formInstance,
    isCurrentStep,
    taskIsClosed,
}) => {
    if (isCurrentStep) {
        return !taskIsClosed ? <CurrentIndicator /> : <CancelledIndicator />
    }
    switch (formInstance?.state) {
        case FormInstanceState.DONE:
            switch (formInstance?.outcome.oneofKind) {
                case 'skipped':
                    return <SkippedIndicator />
                case 'reassigned':
                    return <ReassignedIndicator />
                case 'completed':
                default:
                    return <ApprovedIndicator />
            }
        case FormInstanceState.WAITING:
        default:
            return !taskIsClosed ? <IncompleteIndicator /> : <CancelledIndicator />
    }
}

const ActionStepInstanceIndicator: FC<{
    actionInstance?: ActionInstance,
    isCurrentStep: boolean,
    taskIsClosed: boolean
}> = ({
    actionInstance,
    isCurrentStep,
    taskIsClosed,
}) => {
    if (isCurrentStep && actionInstance?.state !== ActionInstanceState.DONE && actionInstance?.state !== ActionInstanceState.ERROR) {
        return !taskIsClosed ? <CurrentIndicator /> : <CancelledIndicator />
    }
    switch (actionInstance?.state) {
        case ActionInstanceState.DONE:
            switch (actionInstance?.outcome.oneofKind) {
                case 'success':
                    return <ApprovedIndicator />
                case 'denied':
                    return <DeniedIndicator />
                case 'cancelled':
                    return <CancelledIndicator />
                case 'error':
                    return <ErroredIndicator />
                default:
                    return <ApprovedIndicator />
            }
        case ActionInstanceState.ERROR:
            return <ErroredIndicator />
        case ActionInstanceState.INIT:
        case ActionInstanceState.RUNNING:
        default:
            return !taskIsClosed ? <IncompleteIndicator /> : <CancelledIndicator />
    }
}

const getStepTarget = (task: TaskValue, stepType?: string, provision?: Provision, provisionInstanceState?: ProvisionInstance):
    StepTarget => {
    if (!stepType || ['approval', 'accept', 'reject', 'wait', 'form', 'action'].includes(stepType)) {
        return null
    }
    // provision target in a delegated step is the original requested entitlement and not the delegated entilement.
    if (provision && provision.provisionPolicy?.typ.oneofKind === 'delegated') {
        return provision.provisionPolicy.typ.delegated
    }
    if (provisionInstanceState?.provision?.provisionTarget) {
        return {
            appId: provisionInstanceState.provision.provisionTarget.appId,
            entitlementId: provisionInstanceState.provision.provisionTarget.appEntitlementId,
        }
    }
    if (provision?.provisionTarget) {
        return {
            appId: provision.provisionTarget.appId,
            entitlementId: provision.provisionTarget.appEntitlementId,
        }
    }
    if (task.appEntitlements) {
        // if step does not have a provision target use the one on the task
        return {
            appId: task.appEntitlements[0].appId,
            entitlementId: task.appEntitlements[0].id,
        }
    }
    return null
}

type PolicyStepProps = {
    task: TaskValue
    step: StepWithStepInstance
    currentStep: StepWithStepInstance | undefined
    lastChild: boolean
}

export const PolicyStep: FC<PolicyStepProps> = ({
    task,
    step,
    currentStep,
    lastChild,
}) => {
    const intl = useIntl()
    const isCurrent = step.instance?.id === currentStep?.instance?.id
    const approval = step.step.step.oneofKind === 'approval' ? step.step.step.approval : undefined
    const accept = step.step.step.oneofKind === 'accept' ? step.step.step.accept : undefined
    const reject = step.step.step.oneofKind === 'reject' ? step.step.step.reject : undefined
    const provision = step.step.step.oneofKind === 'provision' ? step.step.step.provision : undefined
    const wait = step.step.step.oneofKind === 'wait' ? step.step.step.wait : undefined
    const form = step.step.step.oneofKind === 'form' ? step.step.step.form : undefined
    const action = step.step.step.oneofKind === 'action' ? step.step.step.action : undefined

    const taskIsClosed = !!getOutcome(task) && !currentStep

    const approvalInstanceState =
        step.instance?.instance.oneofKind === 'approval' ? step.instance.instance.approval : undefined
    const provisionInstanceState =
        step.instance?.instance.oneofKind === 'provision' ? step.instance.instance.provision : undefined
    const waitInstanceState =
        step.instance?.instance.oneofKind === 'wait' ? step.instance.instance.wait : undefined
    const formInstanceState =
        step.instance?.instance.oneofKind === 'form' ? step.instance.instance.form : undefined
    const actionInstanceState =
        step.instance?.instance.oneofKind === 'action' ? step.instance.instance.action : undefined

    // Find step provision or deprovision target
    const stepTarget: StepTarget = getStepTarget(task, step.step.step.oneofKind, provision, provisionInstanceState)

    let info
    if (approval) {
        info = <ActionInfo currentStepApprovalInstance={approvalInstanceState} />
    }
    if (provisionInstanceState) {
        info = <ProvisionInfo currentStepProvisionInstance={provisionInstanceState} />
    }
    if (wait) {
        info = <WaitInfo currentWaitInstance={waitInstanceState} />
    }
    if (form) {
        info = <FormInfo currentFormInstance={formInstanceState} />
    }
    if (action) {
        info = <ActionStepInfo currentActionInstance={actionInstanceState} />
    }

    const approvalType = getApprovalTypei18n({ approval, intl })

    const isStepComplete = approvalInstanceState?.state === ApprovalInstanceState.DONE ||
        provisionInstanceState?.state === ProvisionInstanceState.DONE ||
        waitInstanceState?.state === WaitInstance_WaitInstanceState.COMPLETED ||
        formInstanceState?.state === FormInstanceState.DONE ||
        actionInstanceState?.state === ActionInstanceState.DONE ||
        actionInstanceState?.state === ActionInstanceState.ERROR

    const getWaitMessage = (waitStep: Wait, waitInstance?: WaitInstance): string => {
        if (waitStep.until?.oneofKind === 'condition') {
            return intl.formatMessage({
                id: 'task_review_chain_wait_condition',
                defaultMessage: 'Waiting: {name}',
            }, { name: waitStep.name })
        }
        if (waitStep.until?.oneofKind === 'untilTime') {
            if (waitInstance?.until?.oneofKind === 'untilTime' && waitInstance.until.untilTime?.untilTime) {
                const timeString = getShortString({
                    d: pbtsTimestamp.toDate(waitInstance.until.untilTime.untilTime),
                    variant: 'dense',
                })
                if (waitInstance.until.untilTime.durationIfExists) {
                    const durationString = roundedDurationString(intl, waitInstance.until.untilTime.durationIfExists)
                    return intl.formatMessage({
                        id: 'task_review_chain_wait_until_time_with_duration',
                        defaultMessage: 'Waiting for {duration} until {time}',
                    }, { duration: durationString, time: timeString })
                }
                return intl.formatMessage({
                    id: 'task_review_chain_wait_until_time_with_time',
                    defaultMessage: 'Waiting until {time}',
                }, { time: timeString })
            }
            return intl.formatMessage({
                id: 'task_review_chain_wait_until_time',
                defaultMessage: 'Waiting until time',
            })
        }
        // Fallback to the original behavior
        return intl.formatMessage({
            id: 'task_review_chain_wait',
            defaultMessage: 'Waiting: {name}',
        }, { name: waitStep.name })
    }

    const getStepTypeText = (taskValue: TaskValue): string => {
        if (approval) {
            return intl.formatMessage({
                id: 'task_review_chain_approval',
                defaultMessage: '{type} approval',
            }, { type: approvalType })
        }
        if (accept) {
            return intl.formatMessage({
                id: 'task_review_chain_accept',
                defaultMessage: 'Auto-approve this task',
            })
        }
        if (reject) {
            return intl.formatMessage({
                id: 'task_review_chain_reject',
                defaultMessage: 'Auto-deny this task',
            })
        }
        if (wait) {
            return getWaitMessage(wait, waitInstanceState)
        }
        if (form) {
            return intl.formatMessage({
                id: 'task_review_chain_form',
                defaultMessage: 'Collect form data',
            })
        }
        if (action) {
            return intl.formatMessage({
                id: 'task_review_chain_action',
                defaultMessage: 'Run action',
            })
        }

        if (taskValue.type?.taskType.oneofKind === 'grant') {
            // if provisioning entitlement and not app access
            if (stepTarget && taskValue.appEntitlements && taskValue.appEntitlements[0].displayName !== 'access' &&
                stepTarget.entitlementId === taskValue.appEntitlements[0].id) {
                return intl.formatMessage({
                    id: 'task_review_chain_grant',
                    defaultMessage: 'Grant',
                })
            }
            // if provisioning app access.
            return intl.formatMessage({
                id: 'task_review_chain_provision',
                defaultMessage: 'Provision',
            })
        }
        return intl.formatMessage({
            id: 'task_review_chain_deprovision',
            defaultMessage: 'Remove',
        })
    }

    const isPecError = provisionInstanceState?.outcome.oneofKind === 'errored' && provisionInstanceState.outcome.errored?.errorCode === 'PEC-1'
    const pecErrorText = isPecError && <FormattedMessage id="no_account_found_title" defaultMessage="No account found" />
    const stepText = (!isPecError && provision)
        ? getProvisionTypei18n({ provision, task, intl })
        : getStepTypeText(task)

    const hasExternalTicket =
        provisionInstanceState?.externalTicketId &&
        provisionInstanceState?.externalTicketProvisionerConfigId

    const externalTicketDisplay = (!isPecError && provision && hasExternalTicket) &&
        <ExternalTicketProvisionDisplay
            label={stepText}
            provisionInstance={provisionInstanceState}
        />

    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{ position: 'relative' }}
        >
            <Box sx={{ width: '10rem', minHeight: '100%' }}>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {approval && <ApprovalInstanceIndicator
                        approvalInstance={approvalInstanceState}
                        isCurrentStep={isCurrent}
                        taskIsClosed={taskIsClosed}
                    />}
                    {wait && <WaitInstanceIndicator
                        waitInstance={waitInstanceState}
                        isCurrentStep={isCurrent}
                        taskIsClosed={taskIsClosed}
                    />}
                    {provision && <ProvisionInstanceIndicator
                        provisionInstance={provisionInstanceState}
                        isCurrentStep={isCurrent}
                        taskIsClosed={taskIsClosed}
                    />}
                    {form && <FormInstanceIndicator
                        formInstance={formInstanceState}
                        isCurrentStep={isCurrent}
                        taskIsClosed={taskIsClosed}
                    />}
                    {action && <ActionStepInstanceIndicator
                        actionInstance={actionInstanceState}
                        isCurrentStep={isCurrent}
                        taskIsClosed={taskIsClosed}
                    />}
                    {accept && <ApprovedIndicator />}
                    {reject && <DeniedIndicator />}
                </Box>
                {!lastChild && <StepLine isStepComplete={isStepComplete} />}
            </Box>
            <Box sx={{ overflow: 'hidden', flex: 1, pt: 1, pb: lastChild ? 4 : 0 }}>
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        {pecErrorText || <Stack direction="row" spacing={1}>
                            {externalTicketDisplay || <Typography>{stepText}</Typography>}
                        </Stack>}

                    </Stack>

                    {(stepTarget && !taskIsClosed && !isPecError)
                        && <Box mt={1}>
                            <C1EntitlementDisplayNameWithFetch
                                entitlementId={stepTarget.entitlementId}
                                appId={stepTarget.appId}
                                showDrawer
                                excludeDetails
                            />
                        </Box>}
                </Box>
                {(!taskIsClosed && !hasExternalTicket) && <DisplayActionableUserList
                    currentStep={isCurrent}
                    approval={approvalInstanceState}
                    provision={provisionInstanceState}
                    task={task}
                />}
                {step.instance?.instance.oneofKind === 'provision' && <ShowProvisionError provisionInstance={provisionInstanceState} step={step} task={task} />}
                {step.instance?.instance.oneofKind === 'action' && <ShowActionError actionInstance={actionInstanceState} />}
                {info && <section>{info}</section>}

                {!lastChild && <Divider sx={{ my: 4 }} />}
            </Box>
        </Stack>
    )
}

export const ProvisionSteps: FC<ProvisionChainProps> = ({ task }) => {
    const policyInstance = task.policy
    const steps = stepsFromInstance(policyInstance, task)
    const currentStep = currentStepInstance({ steps, policy: policyInstance })
    const provisionSteps = steps.filter((step) => step.step.step.oneofKind === 'provision')

    return provisionSteps.length > 0 && <>
        <Typography variant="h4">
            <FormattedMessage id="components_task_dialog_provision_steps" defaultMessage="Provision steps" />
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Stack spacing={2}>
            {provisionSteps.map((step, i) => <PolicyStep
                key={step.instance?.id || i}
                task={task}
                step={step}
                currentStep={currentStep}
                lastChild={provisionSteps.length - 1 === i}
            />)}
        </Stack>
    </>
}

export const ApprovalSteps: FC<ApprovalChainProps> = ({ task }) => {
    const policyInstance = task.policy
    const steps = stepsFromInstance(policyInstance, task)
    const currentStep = currentStepInstance({ steps, policy: policyInstance })
    const approvalSteps = steps.filter((step) => step.step.step.oneofKind === 'approval' || step.step.step.oneofKind === 'accept' || step.step.step.oneofKind === 'reject' || step.step.step.oneofKind === 'wait' || step.step.step.oneofKind === 'form' || step.step.step.oneofKind === 'action')

    return approvalSteps.length > 0 && <>
        <Typography variant="h4">
            {task.type?.taskType.oneofKind === 'grant'
                ? <FormattedMessage id="components_task_dialog_review_steps" defaultMessage="Approval steps" />
                : task.type?.taskType.oneofKind === 'certify'
                    ? <FormattedMessage id="components_task_dialog_certify_steps" defaultMessage="Certification steps" />
                    : <FormattedMessage id="components_task_dialog_approval_steps" defaultMessage="Confirmation steps" />}
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Stack spacing={2}>
            {approvalSteps.map((step, i) => (
                <PolicyStep
                    key={step.instance?.id || i}
                    task={task}
                    step={step}
                    currentStep={currentStep}
                    lastChild={approvalSteps.length - 1 === i}
                />
            ))}
        </Stack>
    </>
}

type ExternalTicketProvisionDisplayProps = {
    provisionInstance?: ProvisionInstance
    label?: string
}

const ExternalTicketProvisionDisplay: FC<ExternalTicketProvisionDisplayProps> = ({ label, provisionInstance }) => {
    const externalTicketId = provisionInstance?.externalTicketId || ''
    const externalTicketConfigId = provisionInstance?.externalTicketProvisionerConfigId || ''

    const {
        data: externalTicket,
        isLoading: isLoadingExternalTicket,
        error: errorExternalTicket,
    } = GetExternalTicketApi.useGetExternalTicket({
        externalTicketId,
        provisionerConfigId: externalTicketConfigId,
    }, {
        actionable: !!externalTicketId && !!externalTicketConfigId,
    })

    if (isLoadingExternalTicket || errorExternalTicket || !externalTicket?.externalTicket) {
        return null
    }

    const externalTicketLink = <ExternalTicketLink externalTicket={externalTicket.externalTicket} />
    const prefixedLabel = externalTicket.externalTicket?.externalTicketUrl ? <span style={{ whiteSpace: 'pre' }}>{`${label}:`}</span> : label

    return <Stack spacing={2} direction="row" alignItems="center">
        <Stack spacing={2} direction="row">
            <FormattedMessage
                id="external_ticket_provision_display_external_ticket_link"
                defaultMessage="{label}{link}"
                values={{ link: externalTicketLink, label: prefixedLabel }}
            />
        </Stack>
        <ExternalTicketStatus externalTicket={externalTicket.externalTicket} />
    </Stack>
}

const ExternalTicketLink: FC<{ externalTicket: ExternalTicket }> = ({ externalTicket }) => {
    const intl = useIntl()
    if (externalTicket?.externalTicketUrl) {
        const link = linkLabel(externalTicket.externalTicketUrl)
        const linkText = link === externalTicket.externalTicketUrl
            ? intl.formatMessage({ id: 'external_ticket_link_text', defaultMessage: 'External ticket' })
            : link
        return <ExternalLink href={externalTicket.externalTicketUrl}>{linkText}</ExternalLink>
    }
    return null
}

export const ExternalTicketStatus: FC<{ externalTicket: ExternalTicket }> = ({ externalTicket }) => {
    if (!externalTicket) {
        return null
    }

    const { state, lastError } = externalTicket

    if (
        state === ExternalTicketState.ERROR ||
        state === ExternalTicketState.FATAL_ERROR ||
        lastError
    ) {
        return (
            <C1Chip
                color="error"
                label={
                    <FormattedMessage
                        id="components_external_ticket_sync_status_error"
                        defaultMessage="Error"
                    />
                }
            />
        )
    }

    if (
        state === ExternalTicketState.PENDING ||
        state === ExternalTicketState.CREATING
    ) {
        return (
            <C1Chip
                color="info"
                label={
                    <FormattedMessage
                        id="components_external_ticket_sync_status_pending"
                        defaultMessage="Pending creation"
                    />
                }
            />
        )
    }

    if (state === ExternalTicketState.CANCELLED) {
        return (
            <C1Chip
                color="info"
                label={
                    <FormattedMessage
                        id="components_external_ticket_sync_status_canceled"
                        defaultMessage="Canceled"
                    />
                }
            />
        )
    }

    if (state === ExternalTicketState.SKIPPED) {
        return (
            <C1Chip
                color="info"
                label={
                    <FormattedMessage
                        id="components_external_ticket_sync_status_skipped"
                        defaultMessage="Skipped"
                    />
                }
            />
        )
    }

    return (
        <C1Chip
            color="success"
            label={
                <FormattedMessage
                    id="components_external_ticket_sync_status_success"
                    defaultMessage="Success"
                />
            }
        />
    )
}

type ShowProvisionErrorProps = {
    provisionInstance?: ProvisionInstance
    step: StepWithStepInstance
    task: TaskValue
}

const ShowProvisionError: FC<ShowProvisionErrorProps> = ({ provisionInstance, step, task }) => {
    const intl = useIntl()

    const { setTab } = React.useContext(SmartTabsContext)
    const goToAuditLog = (): void => {
        setTab('audit-log')
    }

    if (!provisionInstance) return null

    let erroredAt: pbtsTimestamp | undefined

    switch (provisionInstance.outcome.oneofKind) {
        case 'errored':
            erroredAt = provisionInstance.outcome.errored.erroredAt
            break
        case 'reassignedByError':
            erroredAt = provisionInstance.outcome.reassignedByError.erroredAt
            break
        default:
            return null
    }

    const stepProvision = step.step.step.oneofKind === 'provision' ? step.step.step.provision : undefined
    const isReassignedByError = provisionInstance.outcome.oneofKind === 'reassignedByError'

    const manualProvFallbackError = getManualProvisionFallbackErrori18n({
        stepProvision,
        isReassignedByError,
        task,
        intl,
    })

    let customErrorText

    if (provisionInstance.outcome.oneofKind === 'errored' && provisionInstance.outcome.errored?.errorCode === 'PEC-1') {
        customErrorText = intl.formatMessage({
            id: 'provision_error_app_account_not_found',
            defaultMessage: 'No account found for requesting user. Starting account provisioning process.',
        })
    }

    return (
        <Box>
            <Box sx={{ pt: 1 }}>
                {!(provisionInstance.outcome.oneofKind === 'errored' && provisionInstance.outcome.errored?.errorCode === 'PEC-1') && <Typography variant="caption" color="error">
                    {erroredAt ? (
                        <FormattedMessage
                            id="step_has_error"
                            defaultMessage="Errored on {date}."
                            values={{
                                date: getShortString({ d: pbtsTimestamp.toDate(erroredAt), variant: 'dense' }),
                            }}
                        />
                    ) : (
                        <FormattedMessage id="step_has_error_no_date" defaultMessage="Errored." />
                    )}
                </Typography>}
            </Box>
            {manualProvFallbackError && (<Typography variant="caption">{manualProvFallbackError}</Typography>)}
            <Box sx={{ pt: 1 }}>
                {customErrorText && (<Typography variant="caption">{customErrorText}</Typography>)}
            </Box>
            <Box>
                {!(provisionInstance.outcome.oneofKind === 'errored' && provisionInstance.outcome.errored?.errorCode === 'PEC-1') &&
                <Typography variant="caption" color="error">
                    <FormattedMessage
                        id="step_error_check_audit_log"
                        defaultMessage="<l>Check audit log</l> for more details."
                        values={{
                            l: (chunks) => (
                                <Link onClick={goToAuditLog}>
                                    {chunks}
                                </Link>
                            ),
                        }}
                    />
                </Typography>}

            </Box>
        </Box>
    )
}

type ShowActionErrorProps = {
    actionInstance?: ActionInstance
}

const ShowActionError: FC<ShowActionErrorProps> = ({ actionInstance }) => {
    const { setTab } = React.useContext(SmartTabsContext)
    const goToAuditLog = (): void => {
        setTab('audit-log')
    }

    if (!actionInstance) return null

    // Only show for error state or error outcome
    if (actionInstance.state !== ActionInstanceState.ERROR && actionInstance.outcome?.oneofKind !== 'error') {
        return null
    }

    const erroredAt = actionInstance.outcome?.oneofKind === 'error'
        ? actionInstance.outcome.error.outcomeTime
        : undefined

    return (
        <Box sx={{ pt: 1 }}>
            <Typography variant="caption" color="error">
                {erroredAt ? (
                    <FormattedMessage
                        id="action_step_has_error"
                        defaultMessage="Action errored on {date}."
                        values={{
                            date: getShortString({ d: pbtsTimestamp.toDate(erroredAt), variant: 'dense' }),
                        }}
                    />
                ) : (
                    <FormattedMessage id="action_step_has_error_no_date" defaultMessage="Errored." />
                )}
                <FormattedMessage
                    id="action_step_error_check_audit_log"
                    defaultMessage="See <l>audit log</l> for more details."
                    values={{
                        l: (chunks) => (
                            <Typography variant="caption" color="error">
                                <Link
                                    onClick={goToAuditLog}
                                    color="error"
                                    sx={{
                                        fontSize: 'caption.fontSize',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {chunks}
                                </Link>
                            </Typography>
                        ),
                    }}
                />
            </Typography>
        </Box>
    )
}

const StepLine: FC<{ isStepComplete: boolean }> = ({ isStepComplete }) => {
    return <Box sx={{ position: 'absolute', bottom: '-7px', top: '27px', left: '11px', right: 0, zIndex: 1, width: '2px' }}>
        <svg width="2" height="100%" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <line x1="50%" y1="0" x2="50%" y2="100%" stroke={isStepComplete ? palette.grey[400] : palette.grey[200]} strokeWidth="2" strokeDasharray="5 5" />
        </svg>
    </Box>
}
